import { gql, useLazyQuery, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { Organization } from '@interfaces/organization';
import {
  FulltextSearchSelect,
  type SearchResults,
} from '@ovComponents/2-component/fulltextSearchSelect/fulltextSearchSelect';
import { usePermissions } from '@providers/userContextProvider';
import { useEffect, useState } from 'react';

const SEARCH_ORGANIZATIONS = gql`
  query searchOrganizations($searchText: String!, $childrenFor: ObjectID) {
    fetchOrganizations(input: { filter: { searchText: $searchText, childrenFor: $childrenFor }}) {
      organizations {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
      totalCount
    }
  }
`;

const FETCH_ORGANIZATION_NAME = gql`
  query fetchOrganizationName($organizationId: String!) {
    fetchOrganization(organizationId: $organizationId) {
      organization {
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const OrganizationSelect = ({
  onChange,
  value,
  label,
  size,
  autoFocus,
  childrenFor,
  error,
}: {
  onChange: (event: any) => void;
  value: string | undefined;
  label: string;
  size?: 'small' | 'medium' | undefined;
  autoFocus?: boolean;
  childrenFor?: string;
  error?: boolean;
}) => {
  const { permissions } = usePermissions();
  const [searchOrganizations] = useLazyQuery(SEARCH_ORGANIZATIONS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const [organization, setOrganization] = useState<Organization>({
    id: value ?? '',
  });
  const { loading } = useQuery(FETCH_ORGANIZATION_NAME, {
    skip: !value,
    variables: { organizationId: value },
    onCompleted: (data: any) => {
      setOrganization({
        id: value ?? '',
        translatedName: data.fetchOrganization.organization.translatedName,
      });
    },
  });

  useEffect(() => {
    onChange({ target: { value: organization?.id ?? '' } });
  }, [organization?.id]);

  if (!permissions.includes('read:organizations')) return <></>;

  const searchFn = (searchText: string): Promise<SearchResults> =>
    new Promise<SearchResults>((resolve) => {
      searchOrganizations({
        variables: {
          searchText,
          childrenFor,
        },
        onCompleted: (data) => {
          resolve({
            items: data.fetchOrganizations.organizations,
            count: Number(data.fetchOrganizations.totalCount),
          });
        },
        onError: () => {
          resolve({
            items: [],
            count: 0,
          });
        },
      });
    });

  const generateOrganizationNameString = (org: Organization): string =>
    translateBackend(org.translatedName) ?? '';

  return (
    <FulltextSearchSelect
      label={label}
      size={size}
      disabled={loading}
      error={error}
      item={organization}
      setItem={(o) => setOrganization(o as Organization)}
      searchFn={searchFn}
      primaryLabelFn={generateOrganizationNameString}
      autoFocus={autoFocus}
      fullWidth
    />
  );
};

export default OrganizationSelect;

export const testables = {
  SEARCH_ORGANIZATIONS,
  FETCH_ORGANIZATION_NAME,
};
